import {
	useReactTable,
	getCoreRowModel,
	getFilteredRowModel,
	getExpandedRowModel,
} from '@tanstack/react-table';
import { flexRender } from '@dealstack/frontend/shared/tables/flexRender';
import { useState, useMemo, useRef, CSSProperties } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useSubColumnToggle } from '@dealstack/frontend/app/pages/Valuations/components/table/hooks/useSubColumnToggle/useSubColumnToggle';
import { useColumnFiltering } from '@dealstack/frontend/app/pages/Valuations/components/table/hooks/useColumnFiltering/useColumnFiltering';
import {
	COLUMN_WIDTH,
	ROW_HEIGHT,
	ROW_HEADER_HEIGHT,
} from '@dealstack/frontend/app/pages/Valuations/components/table/constants';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import {
	subColumnsId,
	useGlobalValuationTableColumns,
} from '@dealstack/frontend/app/pages/Valuations/views/ValuationSummary/GlobalViewTable/GlobalValuationTable.utils';
import {
	toGlobalTableData,
	NewGlobalValuationRow,
} from '@dealstack/frontend/app/pages/Valuations/views/ValuationSummary/GlobalViewTable/toGlobalTableData';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ValuationSummaryGroupByMipRes } from '@dealstack/shared/lib/generated';

export function GlobalValuationTable({
	initialData,
	showFilterInputs: externalShowFilterInputs,
	onShowFilterInputsChange,
	customContainerStyles,
	summaryData,
}: {
	initialData: NewGlobalValuationRow[];
	showFilterInputs?: boolean;
	onShowFilterInputsChange?: (show: boolean) => void;
	customContainerStyles?: CSSProperties;
	summaryData?: ValuationSummaryGroupByMipRes;
}) {
	const tableData = initialData;

	const tableContainerRef = useRef<HTMLDivElement>(null);

	const { toggleSubColumns, hasVisibleSubColumns, isLastSubColumn } =
		useSubColumnToggle<NewGlobalValuationRow>();

	const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
		// Initialize sub-columns as hidden by default
		const initialVisibility: Record<string, boolean> = {};

		if (summaryData?.groups) {
			summaryData.groups
				.filter((group) => group.generation > 0 && group.id) // Only groups with sub-columns
				.forEach((group) => {
					group.persons.forEach((person) => {
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						const columnId = subColumnsId({ mipId: group.id!, personId: person.id });
						initialVisibility[columnId] = false; // Hide sub-columns by default
					});
				});
		}

		return initialVisibility;
	});
	const [expanded, setExpanded] = useState<Record<string, boolean>>({});

	const { columnFilters, setColumnFilters, showFilterInputs, updateColumnFilter } =
		useColumnFiltering({
			data: tableData,
			showFilterInputs: externalShowFilterInputs,
			onShowFilterInputsChange,
		});

	const columns = useGlobalValuationTableColumns({
		showFilterInputs,
		columnFilters,
		updateColumnFilter,
		toggleSubColumns,
		hasVisibleSubColumns,
		isLastSubColumn,
		summaryData,
		expandedState: expanded,
		columnVisibility,
	});

	const table = useReactTable<NewGlobalValuationRow>({
		data: tableData,
		columns,
		state: {
			columnVisibility,
			columnFilters,
			expanded,
		},
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onColumnFiltersChange: setColumnFilters,
		onExpandedChange: setExpanded,
		columnResizeMode: 'onChange',
		defaultColumn: {
			minSize: COLUMN_WIDTH,
		},
		getSubRows: (row) => row.subRows ?? [],
		enableExpanding: true,
	});

	const { rows } = table.getRowModel();
	const rowVirtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => tableContainerRef.current,
		estimateSize: () => ROW_HEIGHT,
		overscan: 5,
	});
	const virtualRows = rowVirtualizer.getVirtualItems();
	const totalSize = rowVirtualizer.getTotalSize();

	return (
		<div
			ref={tableContainerRef}
			style={{
				overflow: 'auto',
				margin: 0,
				padding: 0,
				...customContainerStyles,
			}}
		>
			<table
				style={{
					display: 'grid',
					height: '100%',
					width: '100%',
					gridTemplateRows: 'auto 1fr',
					border: 'none',
					borderCollapse: 'collapse',
					borderSpacing: 0,
				}}
			>
				<thead
					style={{
						display: 'grid',
						position: 'sticky',
						top: 0,
						zIndex: Z_INDEX.STICKY_TABLE_HEADER,
					}}
				>
					{table.getHeaderGroups().map((headerGroup) => (
						<tr
							key={headerGroup.id}
							style={{
								display: 'flex',
								height: `${ROW_HEADER_HEIGHT}px`,
								minHeight: `${ROW_HEADER_HEIGHT}px`,
								maxHeight: `${ROW_HEADER_HEIGHT}px`,
								boxSizing: 'border-box',
							}}
						>
							{headerGroup.headers.map((header) =>
								header.isPlaceholder ? null : (
									<th
										key={header.id}
										style={{
											display: 'flex',
											minWidth: COLUMN_WIDTH,
											flex: '1 1 0px',
										}}
									>
										{flexRender(
											header.column.columnDef.header,
											header.getContext()
										)}
									</th>
								)
							)}
						</tr>
					))}
				</thead>

				<tbody
					style={{
						display: 'grid',
						height: `${totalSize}px`,
						position: 'relative',
					}}
				>
					{virtualRows.map((virtualRow) => {
						const row = rows[virtualRow.index];

						return (
							<tr
								key={row.id}
								data-index={virtualRow.index}
								data-testindex={virtualRow.index}
								ref={(node) => rowVirtualizer.measureElement(node)}
								style={{
									display: 'flex',
									position: 'absolute',
									transform: `translateY(${virtualRow.start}px)`,
									width: '100%',
									minHeight: `${ROW_HEIGHT}px`,
									maxHeight: `${ROW_HEIGHT}px`,
								}}
							>
								{row.getVisibleCells().map((cell, cellIndex) => {
									return (
										<td
											key={cell.id}
											data-testindex={cellIndex}
											style={{
												display: 'flex',
												minWidth: COLUMN_WIDTH,
												flex: '1 1 0px',
											}}
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext()
											)}
										</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

export function GlobalValuationTableWithData({
	showFilterInputs,
	onShowFilterInputsChange,
	customContainerStyles,
	summaryByMipData,
}: {
	showFilterInputs?: boolean;
	onShowFilterInputsChange?: (show: boolean) => void;
	customContainerStyles?: CSSProperties;
	summaryByMipData?: ValuationSummaryGroupByMipRes;
} = {}) {
	const tValuations = useTranslationValuations();

	const tableData = useMemo(() => {
		if (summaryByMipData) {
			return toGlobalTableData(summaryByMipData, tValuations);
		}
		return [];
	}, [summaryByMipData, tValuations]);

	return (
		<GlobalValuationTable
			initialData={tableData}
			showFilterInputs={showFilterInputs}
			onShowFilterInputsChange={onShowFilterInputsChange}
			customContainerStyles={customContainerStyles}
			summaryData={summaryByMipData}
		/>
	);
}
